import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import history from 'routing/history';
import Button, { BackButton } from 'components/Button';
import Flag from 'components/Flag';
import useCurrencies from 'products/nomad_insurance/hooks/useCurrencies';
import { getCountry } from 'products/nomad_insurance/assets/data/countries';
import useNextAndPrevUrl from '../../hooks/useNextAndPrevUrl';
import ViewAnimator from '../../components/ViewAnimator';
import { StepHeading } from '../../components/StepHeading/styled';
import SectionHeading from './components/SectionHeading';
import Info from './components/Info';
import Consents from './components/Consents';
import { getSelectedClaim, submitClaim, addValidStep } from '../../state';
import { STEP_PATHS } from '../../consts';
import { SummarySection, SummaryField, SummaryBox } from './styled';
import { NavigationButtons, Alert, File } from '../../styled';

const Summary = () => {
  const dispatch = useDispatch();
  const { claimType, step } = useParams();
  const { nextUrl, prevUrl } = useNextAndPrevUrl(claimType, step);
  const { planholder } = useSelector((state) => state.nomadInsurance.planholder);
  const {
    isLoading,
    claimSubmitted,
    error,
    selectedClaim: claim,
    bankAccount,
    claimID
  } = useSelector((state) => state.nomadInsurance.claims);
  const currencies = useCurrencies();

  const [truthConsent, setTruthConsent] = useState(false);
  const [infoShareConsent, setInfoShareConsent] = useState(false);
  const [processConsent, setProcessConsent] = useState(false);

  useEffect(() => {
    dispatch(getSelectedClaim(claimID));
  }, [dispatch, claimID]);

  useEffect(() => {
    if (truthConsent && infoShareConsent && processConsent) {
      dispatch(addValidStep(STEP_PATHS.SUMMARY));
    }
  }, [dispatch, truthConsent, infoShareConsent, processConsent]);

  useEffect(() => {
    if (claimSubmitted) {
      history.push(nextUrl);
    }
  }, [claimSubmitted]);

  if (!claim) {
    return null;
  }

  const {
    claimantFirstName,
    claimantLastName,
    identityNumber,
    incidentCountry,
    incidentCity,
    claimAmount,
    claimCurrency,
    dateOfService,
    startComment,
    happenedComment,
    claimFiles,
    otherPartiesComment
  } = claim;

  return (
    <ViewAnimator>
      <StepHeading>Review & submit</StepHeading>
      <SummarySection>
        <SectionHeading link="/nomad-insurance/claim">
          Claimant information
        </SectionHeading>
        <SummaryField>
          <b>Name</b>
          <div>{`${claimantFirstName} ${claimantLastName}`}</div>
        </SummaryField>
      </SummarySection>
      <Info
        claimType={claimType}
        dateOfService={dateOfService}
        incidentCity={incidentCity}
        incidentCountry={incidentCountry}
        claimCurrency={claimCurrency}
        claimAmount={claimAmount}
        startComment={startComment}
        happenedComment={happenedComment}
      />
      <SummarySection>
        <SectionHeading
          link={`/nomad-insurance/claim/${claimType}/${STEP_PATHS.OTHER_PARTIES}`}
        >
          Other parties
        </SectionHeading>
        <SummaryField>
          <div>
            <b>
              Have you made a claim against any other party with respect to this event?{' '}
              <br />
              Are you covered under another insurance policy for this type of incident
              (e.g., your own, a spouse's, through work)?
            </b>
          </div>
          {otherPartiesComment?.length > 0 ? (
            <SummaryBox>{otherPartiesComment}</SummaryBox>
          ) : (
            <div>No</div>
          )}
        </SummaryField>
      </SummarySection>
      <SummarySection>
        <SectionHeading
          link={`/nomad-insurance/claim/${claimType}/${STEP_PATHS.PERSONAL_INFORMATION}`}
        >
          Personal information
        </SectionHeading>
        <SummaryField>
          <b>Passport number or national identification number</b>
          <div>{identityNumber}</div>
        </SummaryField>
      </SummarySection>
      <SummarySection>
        <SectionHeading
          link={`/nomad-insurance/claim/${claimType}/${STEP_PATHS.UPLOAD_DOCUMENTS}`}
        >
          Uploaded documents
        </SectionHeading>
        {claimFiles
          ?.filter((file) => file.fileName !== 'DELETED')
          .map((file) => (
            <File key={file.id} color="black">
              {file.userFileName}
            </File>
          ))}
      </SummarySection>
      <SummarySection>
        <SectionHeading
          link={`/nomad-insurance/claim/${claimType}/${STEP_PATHS.REIMBURSEMENT}`}
        >
          Reimbursement
        </SectionHeading>
        <SummaryField>
          <b>In which currency would you like to be reimbursed?</b>
          <div>
            {currencies.find((x) => x.value === bankAccount.reimbursementCurrency)?.label}
          </div>
        </SummaryField>
        <SummaryField>
          <b>Bank name</b>
          <div>{bankAccount.bankName}</div>
        </SummaryField>
        <SummaryField>
          <b>Account holder</b>
          <div>{bankAccount.fullNameOfAccountHolder}</div>
        </SummaryField>
        <SummaryField>
          <b>IBAN</b>
          <div>{bankAccount.accountNumberOrIban}</div>
        </SummaryField>
        <SummaryField>
          <div>
            <b>Bank code</b>
          </div>
          <div>{bankAccount.swift}</div>
        </SummaryField>
        <SummaryField>
          <b>Country</b>
          <div>
            <Flag code={bankAccount.bankCountry} /> {getCountry(bankAccount.bankCountry)}
          </div>
        </SummaryField>
        <SummaryField>
          <b>City</b>
          <div>{bankAccount.bankCity}</div>
        </SummaryField>
      </SummarySection>
      <div>
        <Consents
          truthConsent={truthConsent}
          setTruthConsent={setTruthConsent}
          infoShareConsent={infoShareConsent}
          setInfoShareConsent={setInfoShareConsent}
          processConsent={processConsent}
          setProcessConsent={setProcessConsent}
        />
        {planholder.groupMembers?.length > 0 && (
          <div>
            In your Claims Dashboard, you will be able to access and track the status in
            the claims process for each member/dependent in your group policy.
          </div>
        )}
      </div>
      {error && (
        <Alert variant="danger" label="Something went wrong">
          Something went wrong with saving your information. Please try again, and if the
          problem persists, contact customer service.
        </Alert>
      )}
      <NavigationButtons>
        <BackButton onClick={() => history.push(prevUrl)} variant="dark">
          Back
        </BackButton>
        <Button
          disabled={isLoading || !(truthConsent && infoShareConsent && processConsent)}
          onClick={() => {
            dispatch(submitClaim(claimID));
          }}
        >
          Submit
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default Summary;
